import React, { useState, useEffect, useCallback, useRef } from 'react';
import Toast from './Toast';
import useToast from '../hooks/useToast';
import PronounceBlock from './PronounceBlock';

/**
 * 📤 Posting — die Verteil-Zentrale (Rubrik neben Chat · Recherche · Ideen · Wissen).
 *
 * Bündelt, was vorher über Ideen-Modal und einen Umschalter verstreut war:
 * - Video-Studio: fertige Entwürfe → lokal gerendertes Kurzvideo (kuka2me-video-
 *   Pipeline: kostenlos, deutsche Stimme, Art.-50-Label eingebrannt) — revid nur Fallback
 * - Verteil-Protokoll: welches Thema ging wohin (Ghost live-Status, Video, Kanäle), wann
 * - Kanal-Texte kopieren, Caption + Pexels-Credits fürs Posten
 * KEIN Auto-Publish — Sichten, KI-Schalter der Plattform setzen und posten macht der Mensch.
 */

const CHANNEL_ICON = { linkedin: '💼', newsletter: '✉', teaser: '⚡', video: '🎬' };
const fmtD = iso => iso ? new Date(iso).toLocaleDateString('de-DE') : '—';
const safeHttp = (u) => (u && /^https?:\/\//i.test(u) ? u : undefined);

export default function PostingView({ authToken, onLogout, onHelp, onOpenIdea }) {
  const [ideas, setIdeas]     = useState([]);        // alle Ideen (für Studio: mit Entwurf)
  const [dist, setDist]       = useState(null);      // Verteil-Protokoll
  const [engine, setEngine]   = useState(null);      // {local: bool, revid: bool}
  const [busy, setBusy]       = useState('');
  const [toasts, pushToast]   = useToast();
  const [expanded, setExpanded] = useState(null);    // Idee, deren Kanal-Texte offen sind
  const pollRef = useRef(0);

  const auth = { Authorization: `Bearer ${authToken}` };

  const checked = useCallback((url) => fetch(url, { headers: { Authorization: `Bearer ${authToken}` } }).then(r => {
    if (r.status === 401) { pushToast('Sitzung abgelaufen', 'error'); onLogout?.(); throw new Error('401'); }
    if (!r.ok) throw new Error(String(r.status));
    return r.json();
  }), [authToken, onLogout]);

  const loadAll = useCallback(() => {
    checked('/api/ideas').then(d => setIdeas(d.ideas || [])).catch(() => {});
    setDist(null);
    checked('/api/ideas/distribution').then(d => setDist(d.items || []))
      .catch(() => { setDist('error'); });
    checked('/api/video/engines').then(d => setEngine(d)).catch(() => setEngine({ local: false, revid: false }));
  }, [checked]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Solange ein Video rendert oder eine Aussprache-Prüfung läuft: alle 8s nachladen
  useEffect(() => {
    const rendering = ideas.some(i => i.revid_status === 'rendering' || i.repurpose_status === 'generating'
      || i.repurpose?.pronounce?.status === 'running');
    if (!rendering) return;
    const t = setTimeout(() => { loadAll(); pollRef.current++; }, 8000);
    return () => clearTimeout(t);
  }, [ideas, loadAll]);

  const startVideo = async (id, eng) => {
    setBusy(`vid-${id}`);
    const r = await fetch(`/api/ideas/${id}/video`, {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(eng ? { engine: eng } : {}),
    }).catch(() => null);
    const d = r ? await r.json().catch(() => ({})) : {};
    if (r && (r.ok || r.status === 202)) {
      pushToast(d.engine === 'local'
        ? '🎬 Lokaler Render gestartet — Skript (gemma4) → Aussprache-Check → Stimme (bis 2 Takes) → Untertitel → B-Roll → MP4 (3–5 min, kostenlos)'
        : '🎬 revid-Render gestartet (Cloud, kostet Credits)', 'info');
      loadAll();
    } else {
      pushToast(d.error || 'Video nicht möglich', r && (r.status === 409 || r.status === 400 || r.status === 503) ? 'info' : 'error');
    }
    setBusy('');
  };

  const copyText = (text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => pushToast('In die Zwischenablage kopiert', 'success'),
        () => pushToast('Kopieren nicht möglich', 'error'));
    } else pushToast('Zwischenablage nicht verfügbar', 'error');
  };

  const withDraft = ideas.filter(i => i.draft && (i.status || 'offen') !== 'verworfen');
  const rendering = ideas.filter(i => i.revid_status === 'rendering');
  const videos    = ideas.filter(i => i.revid_video_url);

  return (
    <div className="posting-view">
      <Toast messages={toasts} />

      <div className="page-header">
        <div>
          <h1 className="page-title">Posting</h1>
          <p className="page-sub">Verteil-Zentrale — Videos rendern, Kanal-Texte holen, Verteil-Protokoll. Veröffentlichen tust du.</p>
        </div>
        <div className="page-header-actions">
          {onHelp && <button className="btn-ghost-sm help-btn" onClick={() => onHelp('posting')} title="Hilfe zu dieser Ansicht öffnen">❓</button>}
          {engine && (
            <span className={`posting-engine-chip ${engine.local ? 'posting-engine-on' : ''}`}
              title={engine.local ? 'Lokale Video-Pipeline läuft (kostenlos, deutsche Stimme, Label eingebrannt)'
                : 'Lokale Video-Pipeline nicht erreichbar' + (engine.revid ? ' — revid-Fallback aktiv (Credits)' : '')}>
              🎬 {engine.local ? 'lokal bereit' : (engine.revid ? 'revid-Fallback' : 'keine Engine')}
            </span>
          )}
          <button className="btn-outline" onClick={loadAll} title="Alles neu laden">↻</button>
        </div>
      </div>

      {rendering.length > 0 && (
        <div className="posting-rendering">
          {rendering.map(i => (
            <div key={i.id} className="posting-rendering-item">
              <span className="posting-spinner">⏳</span>
              <span>Video rendert: <strong>{i.title}</strong></span>
              <span className="posting-rendering-hint">
                {i.revid_project_id?.startsWith('local:') ? 'lokal (Aussprache-Check → Stimme → Untertitel → B-Roll → MP4)' : 'revid.ai (Cloud)'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ===== Video-Studio ===== */}
      <section className="posting-section">
        <div className="posting-section-head">
          <h2 className="posting-section-title">🎬 Video-Studio</h2>
          <span className="posting-section-sub">Aus faktengeprüften Entwürfen — lokal, kostenlos, KI-Label dezent eingebrannt</span>
        </div>
        {withDraft.length === 0 ? (
          <div className="empty-state-card">
            <div className="empty-icon">🎬</div>
            <p className="empty-title">Noch kein Entwurf zum Verfilmen</p>
            <p className="empty-sub">Sobald eine Idee einen Entwurf hat (Ideen-Tab → 📄), erscheint sie hier.</p>
          </div>
        ) : (
          <div className="posting-grid">
            {withDraft.map(i => {
              const isRendering = i.revid_status === 'rendering';
              const hasVideo = !!i.revid_video_url;
              const isLocalVideo = (i.revid_video_url || '').startsWith('/api/media/');
              return (
                <div key={i.id} className="posting-card">
                  <button className="posting-card-title" onClick={() => onOpenIdea?.(i.id)} title="Idee im Detail öffnen">{i.title}</button>
                  <div className="posting-card-meta">
                    {i.fact_check ? <span className="meta-chip meta-chip-accent" title="Faktengeprüft">🔬 geprüft</span>
                      : <span className="meta-chip meta-chip-muted" title="Noch kein Faktencheck — Video trotzdem möglich">ungeprüft</span>}
                    {hasVideo && <span className="meta-chip meta-chip-accent">🎬 {isLocalVideo ? 'lokal' : 'revid'}</span>}
                    {i.repurpose?.video && <span className="meta-chip">Skript ✓</span>}
                  </div>

                  {hasVideo && (
                    <div className="posting-video-wrap">
                      {isLocalVideo ? (
                        <video className="posting-video" controls preload="metadata"
                          src={`${i.revid_video_url}?t=${encodeURIComponent(authToken)}`} />
                      ) : (
                        <a href={safeHttp(i.revid_video_url)} target="_blank" rel="noopener noreferrer" className="btn-outline">▶ Video bei revid ansehen</a>
                      )}
                    </div>
                  )}

                  <div className="posting-card-actions">
                    <button className="btn-primary" disabled={!!busy || isRendering || i.repurpose_status === 'generating'}
                      onClick={() => startVideo(i.id)}
                      title={hasVideo ? 'Video neu rendern (lokal kostenlos)' : 'Skript schreiben + Video lokal rendern (2–5 min)'}>
                      {isRendering ? '⏳ rendert…' : hasVideo ? '🎬 Neu rendern' : '🎬 Video erstellen'}
                    </button>
                    {engine?.revid && !isRendering && (
                      <button className="btn-ghost-sm" disabled={!!busy}
                        onClick={() => startVideo(i.id, 'revid')}
                        title="Stattdessen bei revid.ai rendern (Cloud, kostet Credits)">revid</button>
                    )}
                    <button className="btn-ghost-sm" onClick={() => setExpanded(expanded === i.id ? null : i.id)}
                      title="Skript, Caption, Credits & Kanal-Texte zeigen">{expanded === i.id ? '▾' : '▸'} Texte</button>
                  </div>

                  {i.repurpose?.video && (
                    <PronounceBlock idea={i} authToken={authToken} pushToast={pushToast} onChanged={loadAll}
                      engineLocal={!!engine?.local} busy={busy} setBusy={setBusy} />
                  )}

                  {expanded === i.id && (
                    <div className="posting-texts">
                      {i.repurpose?.caption && (
                        <div className="posting-text-block">
                          <div className="posting-text-head"><span>📝 Caption (Plattform-Beschreibung)</span>
                            <button className="btn-ghost-sm" onClick={() => copyText(i.repurpose.caption)} title="Kopieren">⧉</button></div>
                          <pre className="repurpose-result-text">{i.repurpose.caption}</pre>
                        </div>
                      )}
                      {i.repurpose?.video_credits && (
                        <div className="posting-text-block">
                          <div className="posting-text-head"><span>🎞 Footage-Credits (Pexels — in die Caption übernehmen)</span>
                            <button className="btn-ghost-sm" onClick={() => copyText(i.repurpose.video_credits)} title="Kopieren">⧉</button></div>
                          <pre className="repurpose-result-text">{i.repurpose.video_credits}</pre>
                        </div>
                      )}
                      {['video', 'linkedin', 'newsletter', 'teaser'].filter(k => i.repurpose?.[k]).map(k => (
                        <div key={k} className="posting-text-block">
                          <div className="posting-text-head"><span>{CHANNEL_ICON[k]} {k === 'video' ? 'Sprecher-Skript' : k}</span>
                            <button className="btn-ghost-sm" onClick={() => copyText(i.repurpose[k])} title="Kopieren">⧉</button></div>
                          <pre className="repurpose-result-text">{i.repurpose[k]}</pre>
                        </div>
                      ))}
                      {!i.repurpose?.caption && !i.repurpose?.video && (
                        <p className="posting-hint">Noch keine Texte — „🎬 Video erstellen" erzeugt Skript + Caption; weitere Kanäle im Ideen-Detail (♻).</p>
                      )}
                    </div>
                  )}
                  <p className="posting-art50">Vor dem Posten: Video sichten · KI-Schalter der Plattform setzen (Art. 50) · Footage-Credits in die Caption.</p>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ===== Verteil-Protokoll ===== */}
      <section className="posting-section">
        <div className="posting-section-head">
          <h2 className="posting-section-title">📋 Verteil-Protokoll</h2>
          <span className="posting-section-sub">Welches Thema ging wohin — mit Ghost-Live-Status</span>
        </div>
        {dist === null && <div className="knowledge-pending">Lade Protokoll…</div>}
        {dist === 'error' && <div className="empty-state-card"><div className="empty-icon">⚠</div><p className="empty-title">Protokoll nicht ladbar</p><button className="btn-outline" onClick={loadAll}>↻ Erneut</button></div>}
        {Array.isArray(dist) && dist.length === 0 && <div className="reports-empty"><p>Noch nichts verteilt.</p></div>}
        {Array.isArray(dist) && dist.length > 0 && (
          <div className="dist-table" role="table">
            <div className="dist-row dist-head" role="row"><span>Thema</span><span>👻 Ghost</span><span>🎬 Video</span><span>♻ Kanäle</span></div>
            {dist.map(it => (
              <div key={it.id} className="dist-row" role="row">
                <button className="dist-title" onClick={() => onOpenIdea?.(it.id)} title="Idee öffnen">{it.title}</button>
                <span className="dist-cell">
                  {it.ghost_post_id ? (<>
                    <span className={`dist-chip ${it.ghost_live_status === 'published' ? 'dist-chip-pub' : ''}`}>
                      {it.ghost_live_status === 'published' ? '✅ veröffentlicht' : it.ghost_live_status === 'draft' ? '📝 Draft' : '📝 übergeben'}
                    </span>
                    {safeHttp(it.ghost_url) && <a href={safeHttp(it.ghost_url)} target="_blank" rel="noopener noreferrer">öffnen ↗</a>}
                    <span className="dist-date">{fmtD(it.ghost_pushed_at)}</span>
                  </>) : <span className="dist-none">—</span>}
                </span>
                <span className="dist-cell">
                  {it.revid_video_url ? (<>
                    <span className="dist-chip">{(it.revid_video_url || '').startsWith('/api/media/') ? 'lokal' : 'revid'}</span>
                    <span className="dist-date">{fmtD(it.video_rendered_at)}</span>
                  </>) : <span className="dist-none">—</span>}
                </span>
                <span className="dist-cell">
                  {(it.repurpose_channels || []).length
                    ? it.repurpose_channels.filter(c => CHANNEL_ICON[c]).map(c => <span key={c} className="dist-ch">{CHANNEL_ICON[c]}</span>)
                    : <span className="dist-none">—</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {videos.length === 0 && withDraft.length > 0 && (
        <p className="posting-hint" style={{ textAlign: 'center' }}>Tipp: Das erste Video dauert länger (Modelle laden) — danach 1–2 min.</p>
      )}
    </div>
  );
}
